import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_LOCALE, LOCALE_HTML_LANG, resolveInitialLocale, writeStoredLocale, type Locale } from "./locale";
import { dictionaries, type Dictionary } from "./dictionaries";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Typed dictionary for the active locale (e.g. `t.common.signIn`). */
  t: Dictionary;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  // First render is a deterministic DEFAULT_LOCALE — it matches the
  // `<html lang="en">` the SSR shell emits, so hydration never mismatches.
  // The real locale (persisted choice / navigator) resolves right after mount.
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);

  useEffect(() => {
    setLocaleState(resolveInitialLocale());
  }, []);

  // Provider owns `<html lang>` for every React surface, replacing the
  // per-page `document.documentElement.lang` writes that login.tsx did by hand.
  useEffect(() => {
    document.documentElement.lang = LOCALE_HTML_LANG[locale];
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale: (next: Locale) => {
        setLocaleState(next);
        writeStoredLocale(next);
      },
      t: dictionaries[locale]
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within <I18nProvider>");
  }
  return ctx;
}
